import {Component, OnInit, Input, ViewChild, forwardRef} from '@angular/core'
import {
  FormGroup,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  Validators,
} from '@angular/forms'
import {Observable, of, BehaviorSubject} from 'rxjs'
import {
  distinctUntilChanged,
  switchMap,
  debounceTime,
  tap,
  catchError,
  map,
} from 'rxjs/operators'
import {NgSelectComponent} from '@ng-select/ng-select'
import {User} from '@app/core/services/user-management.model'
import {UserService} from '@app/core/services/rest-api'
import {ContextService} from '@app/core/services/context.service'

@Component({
  selector: 'app-user-selector, user-selector',
  templateUrl: './user-selector.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserSelectorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class UserSelectorComponent implements OnInit, ControlValueAccessor {
  @ViewChild(NgSelectComponent, {static: true}) select: NgSelectComponent
  @Input() placeholder = 'Search user'
  @Input() formGroup: FormGroup
  public userList: Observable<User[]>
  public typeahead = new BehaviorSubject<string>(null)
  public listLoading = false
  public loadResultsTotal: number
  public loadResultsCount: number
  public user: User
  public isCurrentInputValid = true
  _onChanged: any

  constructor(
    private userService: UserService,
    private context: ContextService,
  ) {}

  ngOnInit() {
    this.userList = this.typeahead.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      switchMap((term) => this.searchUsers(term)),
    )
  }

  private searchUsers(value: any): Observable<any> {
    let search = ''
    if (value) {
      this.isCurrentInputValid = true
      search = 'email:' + value + '*' + ' OR ' + 'username:' + value + '*'
    }
    return this.userService
      .getUsers({
        domain: this.context.activeDomain,
        search: search,
        results: 50,
      })
      .pipe(
        map((res) => {
          this.loadResultsTotal = res.count
          this.loadResultsCount = res.results.length
          return res.results
        }),
        catchError(() => of([])),
      )
  }

  validateAndAddTag = (event: string) => {
    this.isCurrentInputValid = this.isValidEmail(event)
    if (!this.isCurrentInputValid) {
      return false
    }
    const newUser = {username: '', emailAddress: event}
    this.onChange(newUser)
    this.typeahead.next(null)
    this.isCurrentInputValid = true
    return newUser
  }

  private isValidEmail(email: string): boolean {
    const emailControl = new FormControl(email)
    const validationResult = Validators.email(emailControl)
    return validationResult === null // null means valid, any object means invalid
  }

  private getUser(code: any): Observable<any> {
    if (code == null) {
      return of(null)
    }
    return this.userService
      .getUser(this.context.activeDomain, code)
      .pipe(catchError(() => of(null)))
  }

  onChange(user: User) {
    if (this._onChanged) {
      if (user) {
        this._onChanged({name: user.username, email: user.emailAddress})
      } else {
        this._onChanged(null)
      }
    }
  }

  onOpen() {
    this.typeahead.next(null)
  }

  writeValue(obj: any) {
    if (!obj) {
      this.select.handleClearClick()
    }
    if (obj) {
      this.getUser(obj)
        .pipe(
          tap((domain) => {
            if (domain) {
              this.select.writeValue(domain)
            } else {
              this.select.writeValue(null)
            }
            this.onChange(domain)
          }),
        )
        .subscribe()
    }
  }

  registerOnChange(fn: any) {
    this._onChanged = fn
  }

  registerOnTouched(fn: any) {
    this.select.registerOnTouched(fn)
  }
}
